import {
    Search,
    Database,
    Map,
    GitBranch,
    BarChart3,
    Crosshair,
    FileText,
    Compass,
    Landmark,
    FlaskConical,
    Cpu,
    ShoppingBag,
    Factory,
} from "lucide-react";

export const services = [
    {
        id: "cv-formatting",
        code: "SVC-07",
        icon: FileText,
        title: "CV Formatting",
        badge: "Featured · Fast Start",
        featured: true,
        short: "Professional, client-ready résumé standardisation that presents every candidate with consistency and polish.",
        description:
            "We standardise candidate CVs into professional, client-ready documents — consistent in structure, branded to your guidelines, and rewritten to surface the achievements that matter to hiring panels. Ideal for recruitment firms polishing submissions and professionals who need a CV that performs.",
        deliverables: [
            "Professional, client-ready résumé standardisation",
            "Consistent branding and structure across all submissions",
            "Achievement-led rewriting and credential highlighting",
            "Rapid 24–48 hour turnaround, built for high-volume pipelines",
        ],
    },
    {
        id: "lead-generation",
        code: "SVC-08",
        icon: Compass,
        title: "Lead Generation",
        badge: "Featured · On-Demand",
        featured: true,
        short: "Business and candidate lead generation — verified, compliant and ready to plug into your CRM or outreach engine.",
        description:
            "High-accuracy lead generation for both business development and candidate sourcing: verified contact data, niche audience targeting and CRM-ready delivery that keeps your pipeline full. Tell us who you want to reach — we build the list.",
        deliverables: [
            "Business and candidate lead generation",
            "Verified, compliant contact data",
            "Niche audience and capability targeting",
            "CRM-ready delivery in your preferred format",
        ],
    },
    {
        id: "end-to-end-recruitment",
        code: "SVC-01",
        icon: Search,
        title: "End-to-End Recruitment",
        badge: "Full Lifecycle",
        short: "Complete ownership of the hiring lifecycle — sourcing, screening, interview coordination, offer negotiation and onboarding.",
        description:
            "A single accountable partner from brief to day one. We run the full recruitment lifecycle on your behalf: calibrating the role against the market, sourcing and screening rigorously, coordinating every interview, and managing offer negotiation and onboarding so accepted offers convert into starts.",
        deliverables: [
            "Role calibration and market scoping against your brief",
            "Proactive sourcing across mapped target talent pools",
            "Structured screening and evidence-led shortlists",
            "Interview coordination, feedback loops and offer negotiation",
            "Onboarding support through notice period to start date",
        ],
    },
    {
        id: "talent-pooling",
        code: "SVC-02",
        icon: Database,
        title: "Talent Pooling",
        badge: "Ready-to-Deploy",
        short: "Curated, ready-to-deploy candidate databases built and maintained around your recurring and anticipated hiring needs.",
        description:
            "We build and continuously maintain curated databases of pre-screened, role-ready candidates aligned to your recurring mandates and growth plans — so when a vacancy opens, the search is already halfway done.",
        deliverables: [
            "Curated pools of pre-screened, role-ready candidates",
            "Ongoing engagement that keeps pools warm and current",
            "Rapid deployment the moment a mandate opens",
            "Quarterly refresh and availability audits",
        ],
    },
    {
        id: "talent-mapping",
        code: "SVC-03",
        icon: Map,
        title: "Talent Mapping",
        badge: "Market Intelligence",
        short: "Proactive intelligence on candidates and competitor organisations — gathered ahead of a vacancy, not after it.",
        description:
            "We map target markets, functions and organisations before you need to hire — giving you a live picture of who sits where, how teams are structured, and where the strongest candidates are, well ahead of any vacancy.",
        deliverables: [
            "Full mapping of target organisations and functions",
            "Org-chart intelligence: structures, reporting lines, team composition",
            "Identification of high-potential candidates before a role exists",
            "Perpetual data ownership — the maps are yours to keep",
        ],
    },
    {
        id: "talent-pipelining",
        code: "SVC-04",
        icon: GitBranch,
        title: "Talent Pipelining",
        badge: "Future-Proofing",
        short: "Ongoing dialogue with prospective employees and succession planning support for your most critical roles.",
        description:
            "We keep a warm, continuous dialogue with prospective future employees for your critical and recurring roles — and support succession planning so leadership continuity is never left to chance.",
        deliverables: [
            "Continuous dialogue with prospective future employees",
            "Warm, ranked pipelines for critical and recurring roles",
            "Succession planning support for leadership continuity",
            "Dramatically shorter time-to-hire when a vacancy opens",
        ],
    },
    {
        id: "talent-research-insight",
        code: "SVC-05",
        icon: BarChart3,
        title: "Talent Research & Insight",
        badge: "Primary Research",
        short: "Market mapping, org-chart intelligence, competitor analysis and compensation benchmarking — evidence for sharper decisions.",
        description:
            "Research-grade talent intelligence for strategic decisions: market mapping, competitor and org-chart analysis, and compensation benchmarking that let TA leaders and business heads plan with evidence rather than instinct.",
        deliverables: [
            "Market mapping and talent landscape analysis",
            "Org-chart intelligence and competitor analysis",
            "Compensation benchmarking across functions and geographies",
            "Actionable insight reports for TA and business leadership",
        ],
    },
    {
        id: "executive-research-headhunting",
        code: "SVC-06",
        icon: Crosshair,
        title: "Executive Research & Headhunting",
        badge: "Confidential",
        short: "Confidential senior-level outreach — for retained search firms and direct clients who cannot afford a visible search.",
        description:
            "Discreet identification and confidential approach of senior-level talent, delivered for retained search firms and direct clients alike. We protect your identity, approach passive leaders on your behalf, and assess motivation and fit before any introduction.",
        deliverables: [
            "Confidential senior-level outreach under strict discretion",
            "Support for retained search firms and direct clients",
            "Blind longlists and candidate approach on your behalf",
            "Motivation and fit assessment before introduction",
        ],
    },
];

export const industries = [
    {
        id: "bfsi",
        icon: Landmark,
        name: "BFSI",
        full: "Banking, Financial Services & Insurance",
        tagline: "Capital Markets · Risk · Digital Banking · Insurance",
        paragraph:
            "From investment banks and insurers to fintech challengers, we map and access leadership talent across banking, financial services and insurance. Our work spans risk, compliance, digital banking, asset management and insurance leadership — mandates where regulatory depth and discretion are non-negotiable, across India and global financial centres.",
    },
    {
        id: "pharma",
        icon: FlaskConical,
        name: "Pharma",
        full: "Pharmaceuticals & Life Sciences",
        tagline: "R&D · Regulatory Affairs · Clinical · Commercial",
        paragraph:
            "We support pharmaceutical, biotech and healthcare organisations with research-led search across R&D, regulatory affairs, clinical operations and commercial leadership. These are functions where a wrong hire costs years — our market maps and confidential outreach are built for exactly that precision.",
    },
    {
        id: "it",
        icon: Cpu,
        name: "IT",
        full: "Information Technology",
        tagline: "Enterprise SaaS · Engineering · Product · GTM",
        paragraph:
            "Across enterprise software, IT services and emerging technology, we track engineering, product and go-to-market leadership. We help technology businesses and global capability centres hire ahead of the curve — mapping niche talent clusters before competitors know they exist.",
    },
    {
        id: "retail",
        icon: ShoppingBag,
        name: "Retail",
        full: "Retail & Consumer",
        tagline: "Omnichannel · D2C · Supply Chain · Brand",
        paragraph:
            "From omnichannel retail to D2C and consumer brands, we identify leaders who can run complex supply chains, scale store networks and lead digital transformation — executives who understand both the shop floor and the spreadsheet.",
    },
    {
        id: "manufacturing",
        icon: Factory,
        name: "Manufacturing",
        full: "Manufacturing & Industrial",
        tagline: "Plant Leadership · Operations · Industry 4.0",
        paragraph:
            "We serve industrial and advanced manufacturing clients with searches across plant leadership, operations, engineering and Industry 4.0 transformation roles — building talent intelligence for organisations modernising how things are made.",
    },
];

export const pillars = [
    {
        num: "01",
        title: "Research-led approach",
        text: "We map the market before we search it. Every engagement begins with intelligence — who sits where, at what level, and at what price.",
    },
    {
        num: "02",
        title: "Sector-agnostic expertise",
        text: "We adapt to any industry, not just a fixed list — our research method travels across sectors, geographies and role types.",
    },
    {
        num: "03",
        title: "Full-funnel support",
        text: "From talent pooling and mapping through to offer stage and onboarding, we support the entire hiring funnel — not just the shortlist.",
    },
    {
        num: "04",
        title: "Partner-led delivery",
        text: "Every engagement is overseen directly by our founding partners. Nothing is handed off to junior teams.",
    },
];

export const testimonials = [
    {
        quote: "The CV formatting turnaround was phenomenal — every candidate submission suddenly looked board-ready. It changed how clients perceived our shortlists.",
        name: "[Client Name]",
        title: "[Director]",
        company: "[Recruitment Firm]",
    },
    {
        quote: "Their lead generation filled our pipeline with verified, niche contacts we simply couldn't find ourselves. Accurate, fast and fully compliant.",
        name: "[Client Name]",
        title: "[Head of Business Development]",
        company: "[B2B Services Company]",
    },
    {
        quote: "Vantage Crest works like an extension of our own talent team — discreet, rigorous and remarkably fast. A genuinely research-led partner.",
        name: "[Client Name]",
        title: "[Head of Talent Acquisition]",
        company: "[Enterprise Client]",
    },
];

export const stats = [
    { value: 15, suffix: "+", label: "Years Combined Delivery Experience" },
    { value: null, text: "Diversified", label: "Sector Coverage Across Industries" },
    { value: null, text: "Global", label: "Search Network" },
    { value: 100, suffix: "%", label: "Confidential Engagements" },
];

export const marqueeItems = [
    "CV Formatting",
    "Lead Generation",
    "Talent Mapping",
    "Executive Research",
    "Talent Intelligence",
    "Confidential Search",
    "Talent Pooling",
    "Succession Planning",
    "Compensation Benchmarking",
    "Every Industry",
];
